// LIVE-SAVE-1 (docs/specs/live-savings-1.md §4.1): "is anyone watching the live captions?"
// Three signals, all required for "watched":
//   1. the tab is visible (Page Visibility API — hidden tab, minimised window, locked screen);
//   2. the captions element intersects the viewport (IntersectionObserver — scrolled away, or
//      the record card is off-screen);
//   3. the user hasn't collapsed the captions panel.
// Window blur is deliberately NOT a signal: a caption tab on a second monitor next to the
// meeting app is blurred the whole time and very much watched.
import { useEffect, useState, type RefObject } from 'react';

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function useCaptionsWatched(
  targetRef: RefObject<Element | null>,
  { collapsed, active }: { collapsed: boolean; active: boolean },
): boolean {
  const [visible, setVisible] = useState(pageVisible);
  // Assume on-screen until the observer says otherwise (and where IntersectionObserver is
  // unavailable, e.g. jsdom) — never auto-swap on a signal we can't actually read.
  const [intersecting, setIntersecting] = useState(true);

  useEffect(() => {
    if (!active) return;
    const onChange = () => setVisible(pageVisible());
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [active]);

  useEffect(() => {
    const el = targetRef.current;
    if (!active || !el || typeof IntersectionObserver === 'undefined') {
      setIntersecting(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      const last = entries[entries.length - 1];
      if (last) setIntersecting(last.isIntersecting);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [targetRef, active]);

  return visible && intersecting && !collapsed;
}
